import React, { useEffect, useState } from 'react';
import styles from './index.module.scss';
import * as yup from 'yup';
import { useForm } from 'react-hook-form';
import Header, { CancelButton, RenderAddress } from './components';
import { addressComponents } from './index';
import ActionButton from 'components/ActionButton';
import { addAddress, updateAddress } from 'services';
import { DELIVERY_OPTIONS, showError } from 'utils';
import { Error } from 'components/message/Error';
import { yupResolver } from '@hookform/resolvers';
import DropDown from 'components/FormFields/dropdown';

const validationSchema = yup.object().shape({
  contactName: yup.string().required('Contact Name is a required field'),
  contactPhone: yup.string().required('Contact Phone is a required field'),
});

type FormData = {
  address: string;
  contactName: string;
  contactPhone: string;
  instructions: string;
  deliveryOption: string;
};

const AddressDetails = (props) => {
  const {
    setComponentToShow,
    localAddress,
    setAddressKey,
    user,
    handleClose,
    onSuccess,
  } = props;

  useEffect(()=>{
    setAddressKey({ 
      'contactName':  localAddress.contactName || user?.full_name || '', 
      'contactPhone': localAddress.contactPhone || user?.phone || '', 
    });
  }, [localAddress.contactName, user?.full_name]);
  
  const { register, handleSubmit, errors } = useForm<FormData>({ 
    resolver: yupResolver(validationSchema),
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState<boolean>(false);

  const isValidAddress = () => {
    return !!(localAddress && localAddress.coords && localAddress.address);
  };

  const updateAddressFn = handleSubmit(async () => {
    if (!isValidAddress()) return;
    setLoading(true);
    try {
      let response;
      if(localAddress?.objectId){
        response = await updateAddress({ ...localAddress, id:localAddress?.objectId });
      } else {
        response = await addAddress({
          ...localAddress,
          userId: user.objectId,
        });
      }
      if(response){
        await onSuccess();
      }
      handleClose();
    } catch (e) {
      setError(e);
      showError(e);
    } finally {
      setLoading(false);
    }
  });

  const onClose = () => {
    setComponentToShow(addressComponents.locationDetails);
  };

  const renderError = () => {
    return !!error && <Error message={error} />;
  };

  const renderAddress = () => {
    return(
      <div className={`${styles['form-group']} mb-1`}>
        <label>Address</label>
        <RenderAddress
          address={localAddress?.address} 
          onClick={() => {
            setComponentToShow(addressComponents.searchLocation);
          }}
        />
      </div>
    );
  };

  const renderSecondAddress = () => {
    return(
      <div className={`${styles['form-group']} mb-1`}>
        <label>Second Address</label>
        <input
          name="secondAddress"
          ref={register}
          defaultValue={localAddress.secondAddress}
          disabled={loading}
          onChange={(e) => setAddressKey({ 'secondAddress': e.target.value })}
          placeholder="Unit number, society name"
        />
      </div>
    );
  };

  const renderContactName = () => {
    return(
      <div className={`${styles['form-group']} mb-2 w-md-50 w-100`}>
        <label>Contact Name</label>
        <input
          name="contactName"
          ref={register}
          defaultValue={
            localAddress.contactName || user?.full_name || ''
          }
          disabled={loading}
          onChange={(e) => setAddressKey({ 'contactName': e.target.value })}
          placeholder="Contact name"
        />
        {errors.contactName && <Error message={errors.contactName.message} />}
      </div>
    );
  };

  const renderContactPhone = () => {
    return(
      <div className={`${styles['form-group']} mb-0 w-md-50 w-100 pl-0 pl-md-2`}>
        <label>Contact Number</label>
        <input
          type="number"
          name="contactPhone"
          ref={register}
          defaultValue={
            localAddress.contactPhone || user?.phone || ''
          }
          disabled={loading}
          onChange={(e) => setAddressKey({ 'contactPhone': e.target.value })}
          placeholder="Contact number"
        />
        {errors.contactPhone && (
          <Error message={errors.contactPhone.message} />
        )}
      </div>
    );
  };

  const renderDeliveryOptions = () => {
    const optionDropDownValues = DELIVERY_OPTIONS?.map((option)=> {return { name: option, value: option }}) as any;
    return(
      <div className={`${styles['form-group']} mb-1`}>
        <label>Delivery Options</label>
        <DropDown 
          value={localAddress.deliveryOption} 
          onChange={(e) => setAddressKey({ 'deliveryOption': e })} 
          options={optionDropDownValues} />
      </div>
    );
  };

  const renderInstructions = () => {
    return(
      <div className={`${styles['form-group']} mb-1`}>
        <label>Instructions</label>
        <input
          name="deliveryInstructionText"
          ref={register}
          defaultValue={localAddress.deliveryInstructionText}
          disabled={loading}
          onChange={(e) =>
            setAddressKey({ 'deliveryInstructionText': e.target.value })
          }
          placeholder="Instructions"
        />
      </div>
    );
  };

  const renderFooterButtons = () => {
    return(
      <div className="d-flex align-items-center justify-content-between mt-2">
        <CancelButton onCancel={handleClose} />
        <ActionButton
          text="Save"
          onClick={updateAddressFn}
          isLoading={loading}
          active={!loading}
        />
      </div>
    );
  };
  
  return (
    <div className={`col-12 ${styles['field-container']}`}>
      <Header title="Details" onClose={onClose} />
      {renderAddress()}
      {renderSecondAddress()}
      <div className={'d-flex flex-column flex-md-row'}>
        {renderContactName()}
        {renderContactPhone()}
      </div>
      {renderDeliveryOptions()}
      {renderInstructions()}
      {renderError()}
      {renderFooterButtons()}
    </div>
  );
};
export default AddressDetails;
